/**
 * Hub preferences that are not progression state (and so not in the profile): last chosen key
 * level, dungeon, spectate and affix choice. localStorage, every access in try/catch.
 * The bot preset lives in the profile (`settings.defaultBotPreset`).
 */
import type { AffixId } from '@keystone/sim';
import type { StorageLike } from './profileStore';

/** Run options sheet: affix choice (index stored in prefs; 0 = by key level). */
export const AFFIX_OPTIONS: readonly { label: string; value: AffixId[] | 'auto' }[] = [
  { label: 'By key level', value: 'auto' },
  { label: 'Fortified', value: ['fortified'] },
  { label: 'Tyrannical', value: ['tyrannical'] },
  { label: 'None', value: [] },
];

export type LastVault = 'scales' | 'token' | 'altar';

export const HUB_PREFS_KEY = 'keystone.hub';

export interface HubPrefs {
  level: number;
  dungeonId: string | null;
  spectate: boolean;
  /** Index into the hub's affix options (0 = by key level). */
  affix: number;
  /** Craft: Venom vials added (0..10, remembered). */
  venom: number;
  /** Rank standing the player has seen ("champion:2"); the lobby Rank dot shows until it matches. */
  rankSeen: string | null;
  /** What the last auto-claimed vault paid (Dungeons › Weekly "Last vault" line). */
  lastVault: LastVault | null;
}

export const DEFAULT_HUB_PREFS: HubPrefs = { level: 2, dungeonId: null, spectate: false, affix: 0, venom: 0, rankSeen: null, lastVault: null };

export function sanitizeHubPrefs(raw: unknown): HubPrefs {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  return {
    level: Math.max(2, Math.round(num(o['level'], DEFAULT_HUB_PREFS.level))),
    dungeonId: typeof o['dungeonId'] === 'string' ? o['dungeonId'] : null,
    spectate: o['spectate'] === true,
    affix: Math.max(0, Math.round(num(o['affix'], 0))),
    venom: Math.max(0, Math.min(10, Math.round(num(o['venom'], 0)))),
    rankSeen: typeof o['rankSeen'] === 'string' ? o['rankSeen'] : null,
    lastVault: o['lastVault'] === 'scales' || o['lastVault'] === 'token' || o['lastVault'] === 'altar' ? o['lastVault'] : null,
  };
}

export class HubPrefsStore {
  private value: HubPrefs;
  constructor(private readonly storage: StorageLike | null = defaultStorage(), private readonly key = HUB_PREFS_KEY) {
    let v = DEFAULT_HUB_PREFS;
    try {
      const raw = storage?.getItem(key);
      if (raw) v = sanitizeHubPrefs(JSON.parse(raw));
    } catch {
      /* defaults */
    }
    this.value = v;
  }
  get(): HubPrefs {
    return this.value;
  }
  set(patch: Partial<HubPrefs>): HubPrefs {
    this.value = { ...this.value, ...patch };
    try {
      this.storage?.setItem(this.key, JSON.stringify(this.value));
    } catch {
      /* memory only */
    }
    return this.value;
  }
}

function defaultStorage(): StorageLike | null {
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage ?? null;
  } catch {
    return null;
  }
}
